import { FilterSet } from '../helpers/filters';
import { GeoInfo, geoInfoToUrlParams } from '../services/geo-info';
import { Business } from '../types/business';
import { buildApiUrl } from '../helpers/urls';
import { convertUCSBusinessToBusiness } from './business-registry/business';
import DistanceUnit from './location/distance-units';
import { PaginationDataSet } from '../types/pagination';

export interface SearchRequest {
  searchUrl: string;
  businessRegistryUrl: string;
  geoInfo: GeoInfo;
  sectors: string[];
  types: string[];
  trades: string[];
  classifications: string[];
  query: string;
  page: number;
  pageSize: number;
  radius: number;
  unit?: DistanceUnit;
  businessFloor?: number;
  sort?: string;
  loggingContext: string;
  onlyOfficeAddress?: boolean;
}

export interface SearchResultData {
  results: Business[];
  count: number;
  finalRadius?: number;
}

export const addLatLngToBusiness = (business, addresses) => {
  const latLng = addresses.find((a) => a.id === business.id);
  return {
    ...business,
    latLng: !latLng ? null : latLng.center,
  };
};

export const fetchSearch = async (
  location?: GeoInfo,
  query?: string,
  filters?: FilterSet,
  paginationData?: PaginationDataSet,
  businessFloor?: number,
  radius?: number
): Promise<SearchResultData> => {
  const params = geoInfoToUrlParams(location);

  if (query) {
    params.push(`q=${encodeURIComponent(query)}`);
  }

  const sectors = [];
  const types = [];
  const trades = [];
  const classifications = [];
  if (filters) {
    // Add other filter handling here.
    if (filters.onlyOfficeAddress) {
      params.push('onlyOfficeAddress=true');
    }
    if (filters.constructionSector && filters.constructionSector.length > 0) {
      filters.constructionSector.forEach((filter) => {
        sectors.push(filter.key);
      });
      params.push(`sector=${sectors.join(',')}`);
    }
    if (filters.companyType && filters.companyType.length > 0) {
      filters.companyType.forEach((filter) => {
        types.push(filter.key);
      });
      params.push(`businessType=${types.join(',')}`);
    }
    if (filters.trade && filters.trade.length > 0) {
      filters.trade.forEach((filter) => {
        trades.push(filter.key);
      });
      params.push(`trade=${trades.join(',')}`);
    }
    if (filters.classifications && filters.classifications.length > 0) {
      filters.classifications.forEach((filter) => {
        classifications.push(filter.key);
      });
      params.push(`classifications=${classifications.join(',')}`);
    }
    if (filters.sortBy) {
      params.push(`sort=${filters.sortBy.key}`);
    }
  }
  if (businessFloor) {
    params.push(`businessFloor=${businessFloor}`);
  }

  if (radius) {
    params.push(`radius=${radius}`);
  }
  if (paginationData) {
    params.push(
      `page=${paginationData.page}&pageSize=${paginationData.pageSize}`
    );
  }

  const url = buildApiUrl(`search?${params.join('&')}`);
  let result;
  let json;
  if (url.length < 12000) {
    result = await fetch(url);
    json = await result.json();
  } else {
    json = {
      results: [],
      count: 0,
      finalRadius: 0,
    };
  }

  /* istanbul ignore next */
  return {
    results: (json.results || []).map(convertUCSBusinessToBusiness),
    count: json.count,
    finalRadius: json.finalRadius,
  };
};
